import {
  Heading,
  SelectDropdown,
  SortDropdownPagination,
} from "@/common/components";
import { useEffect, memo, useState } from "@/common/hooks";
import { sortByName } from "@/utils/Utilities";

const AccessOptions = ({
  enrollmentFlow = true,
  selectedEntity,
  selectedIds,
  setSelectedIds,
  accessProfiles,
  insuranceCompanies,
  allProductManufacturers,
  productManufacturers,
  approveAccessProfile,
  companyType,
  setCompanyType,
  labels,
  showHeading = true,
}) => {
  const getAccessLevel = (index, currentAccess) => {
    const profiles = accessProfiles?.accessProfiles;
    if (profiles) {
      let data = profiles.filter((e) => e.typeCode === selectedEntity);

      sortByName(data);

      if (approveAccessProfile && currentAccess) {
        data = data.filter((e) => e.name !== currentAccess);
      } else if (approveAccessProfile && approveAccessProfile[index]) {
        data = data.filter((e) => e.id !== approveAccessProfile[index].id);
      }

      if (!enrollmentFlow) {
        data.unshift({
          id: 0,
          name: "No Access",
        });
      }
      return data;
    } else {
      return [];
    }
  };

  const getProductManufacturersForEntity = () => {
    const obj = {
      I: productManufacturers,
      R: allProductManufacturers,
    };
    return obj[selectedEntity];
  };

  const getApproveAccessProfile = (e) => {
    if (Array.isArray(approveAccessProfile)) {
      return (
        approveAccessProfile.find((e1) => e1.productSponsorId === e.id)?.name ||
        ""
      );
    }
    return "";
  };
  const getProductManufacturerName = () => {
    if (productManufacturers?.length) {
      return productManufacturers[0]?.name;
    } else {
      return "";
    }
  };

  useEffect(() => {
    const companies = insuranceCompanies?.insuranceCompanies;
    if (companies && companies?.length) {
      setCompanyType(companies[0].id);
    }
  }, [insuranceCompanies]);

  const [sortOption, setSortOption] = useState("Name (A to Z)");
  const rawEntities =
    selectedEntity == "I" ? productManufacturers : allProductManufacturers;
  const sortedEntities = [...(rawEntities || [])].sort((a, b) => {
    if (sortOption === "Name (A to Z)") {
      return a.name.localeCompare(b.name);
    } else if (sortOption === "Name (Z to A)") {
      return b.name.localeCompare(a.name);
    }
    return 0;
  });

  const allEntities = sortedEntities;
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 3;

  const startIndex = currentPage * itemsPerPage;
  const visibleEntities = allEntities.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  /* ===================== ADDED: Enable sorting only on specific URL ===================== */
  const [isReqNewOpAccess, setIsReqNewOpAccess] = useState(false);
  useEffect(() => {
    const href =
      typeof window !== "undefined" ? window.location.href.toLowerCase() : "";
    setIsReqNewOpAccess(href.includes("request-new-operator-access"));
  }, []);
  /* ===================================================================================== */

  /* ===================== ADDED: Desktop table sorting (gated by URL) ===================== */
  const naturalCompare = (a = "", b = "") =>
    a.toString().localeCompare(b.toString(), undefined, {
      numeric: true,
      sensitivity: "base",
    });

  const [desktopSort, setDesktopSort] = useState({
    key: "name", // "name" | "current"
    dir: "asc", // "asc" | "desc"
  });

  const toggleDesktopSort = (key) => {
    if (!isReqNewOpAccess) return; // guard
    setDesktopSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
  };

  const getSortedManufacturersForDesktop = () => {
    const list = getProductManufacturersForEntity() || [];
    const copy = [...list];

    copy.sort((a, b) => {
      let va = "";
      let vb = "";

      if (desktopSort.key === "name") {
        va = a.name || "";
        vb = b.name || "";
      } else if (desktopSort.key === "current") {
        va = getApproveAccessProfile(a) || "";
        vb = getApproveAccessProfile(b) || "";
      }

      const cmp = naturalCompare(va, vb);
      return desktopSort.dir === "asc" ? cmp : -cmp;
    });

    return copy;
  };

  const SortIndicator = ({ colKey }) => {
    if (!isReqNewOpAccess || desktopSort.key !== colKey) return null;
    return (
      <span className="ms-1" aria-hidden="true">
        {desktopSort.dir === "asc" ? "▲" : "▼"}
      </span>
    );
  };
  /* ====================================================================================== */

  return (
    <>
      {showHeading && (
        <Heading
          text={labels?.accessOptions}
          wrapperClassNames="h4 border-bottom-1 pb-2 mb-3"
          title={false}
        />
      )}
      {/* Only For Enrollment Flow */}
      {selectedEntity === "I" && enrollmentFlow && (
        <SelectDropdown
          id={"insurance-company"}
          label={labels?.yourInsuranceCompany}
          dropdownData={insuranceCompanies?.insuranceCompanies}
          selectClassName="w-100 py-2"
          select={(e) => {
            setCompanyType(String(e));
          }}
          value={companyType}
          optionId="id"
          optionLabel="companyName"
          ariaLabel="Insurance Company"
        />
      )}

      {["I", "R"].indexOf(selectedEntity) > -1 && (
        <>
          {getProductManufacturersForEntity() &&
          getProductManufacturersForEntity().length ? (
            <>
              <div
                className={`${
                  getProductManufacturersForEntity().length > 5
                    ? "scrollable-table"
                    : ""
                }`}
              >
                <table className="table d-none d-md-table">
                  <thead>
                    <tr>
                      <th
                        className="col-5"
                        role={isReqNewOpAccess ? "button" : undefined}
                        onClick={
                          isReqNewOpAccess
                            ? () => toggleDesktopSort("name")
                            : undefined
                        }
                        aria-sort={
                          isReqNewOpAccess && desktopSort.key === "name"
                            ? desktopSort.dir === "asc"
                              ? "ascending"
                              : "descending"
                            : undefined
                        }
                        title={
                          isReqNewOpAccess
                            ? labels.sortbymanufacturer
                            : undefined
                        }
                      >
                        {labels?.productManufacturer}
                        <SortIndicator colKey="name" />
                      </th>

                      {approveAccessProfile && (
                        <th
                          className="col-3 ps-0"
                          role={isReqNewOpAccess ? "button" : undefined}
                          onClick={
                            isReqNewOpAccess
                              ? () => toggleDesktopSort("current")
                              : undefined
                          }
                          aria-sort={
                            isReqNewOpAccess && desktopSort.key === "current"
                              ? desktopSort.dir === "asc"
                                ? "ascending"
                                : "descending"
                              : undefined
                          }
                          title={
                            isReqNewOpAccess
                              ? labels.sortbycurrentaccesslevel
                              : undefined
                          }
                        >
                          {labels?.currentAccessLevel}
                          <SortIndicator colKey="current" />
                        </th>
                      )}

                      <th className="col-4 access-level">
                        {labels?.accessLevel}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isReqNewOpAccess
                      ? getSortedManufacturersForDesktop()
                      : getProductManufacturersForEntity() || []
                    ).map((e, index) => {
                      const baseList = getProductManufacturersForEntity() || [];
                      // Keep selectedIds aligned to original order when sorted:
                      const originalIndex = isReqNewOpAccess
                        ? baseList.findIndex((x) => x.id === e.id)
                        : index;

                      return (
                        <tr key={e.id}>
                          <td className="col-5 align-content-center">
                            {e.name}
                          </td>
                          {approveAccessProfile && (
                            <td className="col-3 align-content-center">
                              {getApproveAccessProfile(e)}
                            </td>
                          )}
                          <td className="col-4">
                            <SelectDropdown
                              id={e.id}
                              dropdownData={getAccessLevel(
                                "",
                                getApproveAccessProfile(e)
                              )}
                              selectClassName="w-100 py-2"
                              wrapperClassName="mb-0"
                              select={(e1) => {
                                selectedIds[selectedEntity][originalIndex] = {
                                  productMfgId: e.id,
                                  accessProfileId: e1,
                                };
                                setSelectedIds({ ...selectedIds });
                              }}
                              value={
                                selectedIds[selectedEntity][originalIndex]
                                  ?.accessProfileId
                              }
                              optionId="id"
                              optionLabel="name"
                              defaultOption={true}
                              ariaLabel="Access Level"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile view unchanged */}
              <div className="d-md-none">
                {selectedEntity === "R" && (
                  <SortDropdownPagination
                    allEntities={allEntities}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    showSortDropDown={true}
                    itemsPerPage={3}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    labels={labels}
                    dropdownData={["Name (A to Z)", "Name (Z to A)"]}
                  />
                )}

                {selectedEntity === "I" && (
                  <SortDropdownPagination
                    allEntities={allEntities}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    showSortDropDown={true}
                    itemsPerPage={3}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    labels={labels}
                    dropdownData={["Name (A to Z)", "Name (Z to A)"]}
                  />
                )}

                <table className="table">
                  <tbody>
                    {visibleEntities.map((entity, index) => {
                      const globalIndex = startIndex + index;
                      return (
                        <>
                          <tr key={entity.id}>
                            <th className="border-bottom-0">
                              {labels?.productManufacturer}
                            </th>
                            <td className="border-bottom-0 text-end">
                              {entity.name}
                            </td>
                          </tr>
                          {approveAccessProfile && (
                            <tr>
                              <th className="border-bottom-0">
                                {labels?.currentAccessLevel}
                              </th>
                              <td className="border-bottom-0 text-end">
                                {approveAccessProfile[globalIndex]?.name}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <th>{labels?.accessLevel}</th>
                            <td>
                              <SelectDropdown
                                id={entity.id}
                                dropdownData={getAccessLevel(globalIndex)}
                                selectClassName="w-100 py-2"
                                wrapperClassName="mb-0"
                                select={(e1) => {
                                  selectedIds[selectedEntity][globalIndex] = {
                                    productMfgId: entity.id,
                                    accessProfileId: e1,
                                  };
                                  setSelectedIds({ ...selectedIds });
                                }}
                                value={
                                  selectedIds[selectedEntity][globalIndex]
                                    ?.accessProfileId
                                }
                                optionId="id"
                                optionLabel="name"
                                defaultOption={true}
                              />
                            </td>
                          </tr>
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="form-control text-center p-5">
              {labels?.productManufacturerDoesNotExist}
            </div>
          )}
        </>
      )}

      {selectedEntity === "P" && (
        <>
          {!enrollmentFlow ? (
            <>
              <table className="table d-none d-md-table">
                <thead>
                  <tr>
                    <th scope="col">{labels?.productManufacturer}</th>
                    {approveAccessProfile && (
                      <th scope="col">{labels?.currentAccessLevel}</th>
                    )}
                    <th scope="col">{labels?.accessLevel}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="col-5 align-content-center">
                      {getProductManufacturerName()}
                    </td>
                    <td className="col-3 align-content-center">
                      {approveAccessProfile[0]?.name}
                    </td>
                    <td>
                      <SelectDropdown
                        id="accessLevel"
                        dropdownData={getAccessLevel(0)}
                        wrapperClassName="mb-0"
                        selectClassName="w-100 py-2"
                        select={(e) => {
                          selectedIds[selectedEntity][0].accessProfileId = e;
                          setSelectedIds({ ...selectedIds });
                        }}
                        value={selectedIds[selectedEntity][0]?.accessProfileId}
                        optionId="id"
                        optionLabel="name"
                        defaultOption={true}
                        ariaLabel="Access Level"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="d-md-none">
                <table className="table">
                  <tbody>
                    {visibleEntities.map((entity, index) => {
                      const globalIndex = startIndex + index;
                      return (
                        <>
                          <tr key={entity.id}>
                            <th className="border-bottom-0">
                              {labels?.productManufacturer}
                            </th>
                            <td className="border-bottom-0 text-end">
                              {entity.name}
                            </td>
                          </tr>
                          {approveAccessProfile && (
                            <tr>
                              <th className="border-bottom-0">
                                {labels?.currentAccessLevel}
                              </th>
                              <td className="border-bottom-0 text-end">
                                {approveAccessProfile[globalIndex]?.name}
                              </td>
                            </tr>
                          )}
                          <tr>
                            <th>{labels?.accessLevel}</th>
                            <td>
                              <SelectDropdown
                                id={entity.id}
                                dropdownData={getAccessLevel(globalIndex)}
                                selectClassName="w-100 py-2"
                                wrapperClassName="mb-0"
                                select={(e1) => {
                                  selectedIds[selectedEntity][globalIndex] = {
                                    productMfgId: entity.id,
                                    accessProfileId: e1,
                                  };
                                  setSelectedIds({ ...selectedIds });
                                }}
                                value={
                                  selectedIds[selectedEntity][globalIndex]
                                    ?.accessProfileId
                                }
                                optionId="id"
                                optionLabel="name"
                                defaultOption={true}
                              />
                            </td>
                          </tr>
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <>
              <SelectDropdown
                id="productManufacturer"
                label={labels?.productManufacturer + "*"}
                dropdownData={allProductManufacturers}
                selectClassName="w-100 py-2"
                select={(e) => {
                  selectedIds[selectedEntity][0].productMfgId = e;
                  setSelectedIds({ ...selectedIds });
                }}
                value={selectedIds[selectedEntity][0]?.productMfgId}
                optionId="id"
                optionLabel="name"
                ariaLabel="Product Manufacturer"
              />

              <SelectDropdown
                id="accessLevel"
                label={labels?.accessLevel + "*"}
                dropdownData={getAccessLevel()}
                selectClassName="w-100 py-2"
                wrapperClassName="mb-3"
                select={(e) => {
                  selectedIds[selectedEntity][0].accessProfileId = e;
                  setSelectedIds({ ...selectedIds });
                }}
                value={selectedIds[selectedEntity][0]?.accessProfileId}
                optionId="id"
                optionLabel="name"
                defaultOption={true}
                ariaLabel="Access Level"
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default memo(AccessOptions);
